import React, {Component} from "react"
import {connect} from "react-redux";
import axios from "axios";
import {setInitialData} from "../actions/initialDataActions";
import {Container, Loader, Message} from "semantic-ui-react";
import {
    ELIGIBILITY_LOSS,
    ENROLLMENT_ENDED,
    PAYMENT_REQUIRED,
    resolveErrorMessage,
    SUCCESSFUL_PAYMENT,
    GENERIC_CONSENT_ROUTE,
    STATE_SPECIFIC_CONSENT_ROUTE,
    PLAN_ROUTE,
} from "../utils/common";
import {withRouter} from "react-router-dom";
import {setClaimList, setNIGOClaims} from "../actions/claimsActions";
import {setAOEInitialDependents, setAOEStatus} from "../actions/anualOpenEnrollmentActions";
import {setLanguage} from "../actions/userActions";
import {languages} from "../utils/localization";
import {setLastRoute} from "../actions/navigationActions";
import moment from "moment";
import {setBillingData} from "../actions/billingActions";
import {setIsStopscreenDisplayed} from "../actions/stopScreensActions";
import {setEConsentData, setEConsentRevokeLogsOut} from "../actions/eConsentActions";

const mapStateToProps = (state, ownProps) => {
    const {auth} = state;
    return {
        ...ownProps,
        auth: auth,
        contactSfId: !!auth.loginInfo ? auth.loginInfo.contactSfId : null,
    }
};

class PrepareApp extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            prepareError: '',
        }
    }

    componentDidMount() {
        this.init().then();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        this.props.dispatch(setLastRoute(prevProps.location.pathname))
    }

    init = async () => {
        const {auth} = this.props;
        const {loginInfo} = auth;

        let missingLoginToken = !loginInfo || !loginInfo.authToken;

        if (missingLoginToken) {
            this.props.history.replace("/login");
        } else if (!auth.mfaEnabled && auth.mfaNotEnabledGracePeriodEnded) {
            this.props.history.replace("/mfa");
        } else {
            try {
                this.setState({loading: true})

                const postEndpointConfig = {headers: {'Content-Type': 'application/json'}}
                const [
                    {data: {claims}},
                    {data: prepareAppData},
                    {data: billingInfo},
                ] = await Promise.all([
                    axios.post("/api/member/v2/getClaims", null, postEndpointConfig),
                    axios.post("/api/member/v2/prepareAppMember", null, postEndpointConfig),
                    axios.post("/api/member/v2/prepareBilling", null, postEndpointConfig),
                ]);

                const notInGoodOrderClaims = (!!claims && claims.length > 0) ? claims.filter(claim => claim.status === "Not in Good Order") : [];

                this.props.dispatch(setClaimList(claims));
                this.props.dispatch(setNIGOClaims(notInGoodOrderClaims));

                if (prepareAppData.redirectAuthenticated) {
                    window.location = prepareAppData.redirectAuthenticated;
                    return;
                }

                const userIsCertificateHolder = this.props.contactSfId === prepareAppData.certificateHolder.id;
                let user = null;

                if (userIsCertificateHolder) {
                    user = prepareAppData.certificateHolder
                } else if (!!prepareAppData.dependents && prepareAppData.dependents.length > 0) {
                    const dependent = prepareAppData.dependents.filter(dependent => dependent.id === this.props.contactSfId)
                    user = dependent[0]
                }

                const payload = {
                    dependents: prepareAppData.dependents,
                    user: user,
                    employer: prepareAppData.employer,
                    certificateHolder: prepareAppData.certificateHolder,
                    disclaimers: prepareAppData.disclaimers,
                    limitations: prepareAppData.limitations,
                    doctorsOfficeOptions: prepareAppData.doctorsOfficeOptions,
                    urgentCareOptions: prepareAppData.urgentCareOptions,
                    facilityOptions: prepareAppData.facilityOptions,
                    documents: prepareAppData.documents,
                    cost: prepareAppData.cost,
                    employerContribution: prepareAppData.employerContribution,
                    payrollFrequency: prepareAppData.payrollFrequency,
                    certificate: prepareAppData.certificate,
                    userIsCertificateHolder: userIsCertificateHolder,
                    qleReasonOptions: prepareAppData.qleReasonOptions,
                    contract: prepareAppData.contract,
                    increments: prepareAppData.increments,
                    eligibility: prepareAppData.certificate.ratedClass.contractTerms.eligibility,
                    quoteInfo: prepareAppData.quoteInfo,
                    employerContributions: prepareAppData.employerContributions,
                    presets: prepareAppData.certificate.ratedClass.contractTerms.presets,
                    coverageHistory: prepareAppData.coverageHistory,
                    futurePendingAsset: prepareAppData.futurePendingAsset,
                    openEnrollment: prepareAppData.openEnrollment,
                    hasRestrictedAccess: prepareAppData.hasRestrictedAccess,
                    isBillingEnabled: prepareAppData.isBillingEnabled,
                    stopScreenInfo: prepareAppData.stopScreenInfo,
                    isOpenEnrollment: prepareAppData.isOpenEnrollment,
                    isNotEligible: prepareAppData.isNotEligible,
                    riderLabels: prepareAppData.riderLabels,
                    benefitLimits: prepareAppData.benefitLimits,
                    groupTypeLabels: prepareAppData.groupTypeLabels,
                }

                this.props.dispatch(setInitialData(payload));
                this.props.dispatch(setLanguage(languages.ENGLISH));
                this.props.dispatch(setAOEInitialDependents(prepareAppData.dependents));
                this.props.dispatch(setAOEStatus(prepareAppData.annualOpenEnrollmentStatus));
                this.props.dispatch(setIsStopscreenDisplayed(false));
                if (billingInfo) {
                    this.props.dispatch(setBillingData(billingInfo));
                }

                const {isBillingEnabled, stopScreenInfo} = payload;

                const updateStateAndRedirect = (path) => {
                    this.setState(
                        {loading: false, prepareError: ''},
                        () => this.props.history.push(path)
                    );
                }

                if (stopScreenInfo?.isEligibilityLoss) {
                    updateStateAndRedirect(ELIGIBILITY_LOSS);
                    return;
                }

                const paymentStatus = stopScreenInfo?.enrollmentPaymentStatus;
                const isPaymentStatusNeeded = paymentStatus === 'Needed';

                const bankAccountPaymentStatus = stopScreenInfo?.bankAccountPaymentStatus;
                const isBankAccountPaymentStatusPending = bankAccountPaymentStatus === "pending";

                const effectiveDate = moment(payload.certificate.effectiveDate);
                const enrollmentPaymentDueDate = moment(payload.certificate.enrollmentPaymentDueDate);
                const isEnrollmentPaymentDueDateBeforeNow = enrollmentPaymentDueDate.isBefore(moment(), 'day');
                const isEffectiveDateBeforeNowOrToday = effectiveDate.isSameOrBefore(moment(), 'day');

                const end = moment(prepareAppData.certificate.enrollmentEndDate);
                const isEndDateBeforeNow =  end.isBefore(moment());

                if (isBillingEnabled) {
                    if (isPaymentStatusNeeded) {
                        if (isEnrollmentPaymentDueDateBeforeNow && payload.certificate.status === "Voided") {
                            updateStateAndRedirect(ENROLLMENT_ENDED);
                            return;
                        } else {
                            const path = isBankAccountPaymentStatusPending ? SUCCESSFUL_PAYMENT : PAYMENT_REQUIRED;
                            updateStateAndRedirect(path);
                            return;
                        }
                    } else if (!isEndDateBeforeNow && !isEffectiveDateBeforeNowOrToday) {
                        updateStateAndRedirect(SUCCESSFUL_PAYMENT);
                        return;
                    }
                }
                if (!prepareAppData.hasRestrictedAccess) {
                    await this.handleEConsent(prepareAppData.eConsent)
                }
                this.setState({loading: false, prepareError: ''})
            } catch (e) {
                console.warn(e)
                this.setState({loading: false, prepareError: resolveErrorMessage(e, "An unexpected error ocurred")})
            }
        }
    }

    handleEConsent = async (eConsentData) => {
        const {dispatch, location, history} = this.props;
        const {signed, step, revokeLogsOut} = eConsentData;
        dispatch(setEConsentData(eConsentData))
        dispatch(setEConsentRevokeLogsOut(revokeLogsOut))
        let targetRoute = PLAN_ROUTE;
        if (!signed && step === "genericConsent") {
            targetRoute = GENERIC_CONSENT_ROUTE;
        } else if (!signed && step === "docusignConsent") {
            targetRoute = STATE_SPECIFIC_CONSENT_ROUTE;
        }
        if (location.pathname !== targetRoute) {
            history.push(targetRoute);
        }
    };

    render() {
        if (this.state.loading) return <Loader active/>

        if (this.state.prepareError.length > 0) return (<Container style={{marginTop: '2em'}}>
            <Message negative>{this.state.prepareError}</Message>
        </Container>)

        return <React.Fragment>{this.props.children}</React.Fragment>
    }
}

export default connect(mapStateToProps)(withRouter(PrepareApp));
